<script>
import axios from 'axios'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
} from 'chart.js'
import moment from 'moment'

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale, Filler)

const monthNames = [
  'led', 'úno', 'bře', 'dub', 'kvě', 'čvn',
  'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'
]

export default {
  name: 'PageCzeReg',
  components: { Line },

  data() {
    return {
      period: 'week',  // 'week' | 'month'
      loading: false,
      rows: [],
      chartKey: 0,
    }
  },

  computed: {
    chartData() {
      const labels = this.rows.map(r => this.formatLabel(r))
      return {
        labels,
        datasets: [
          {
            label: 'Registrovaní',
            data: this.rows.map(r => r.registered),
            borderColor: '#5aacff',
            backgroundColor: 'rgba(90,172,255,0.08)',
            tension: 0.3,
            fill: false,
            pointRadius: 4,
          },
          {
            label: 'Trialisté',
            data: this.rows.map(r => r.trialists),
            borderColor: '#ffa040',
            backgroundColor: 'rgba(255,160,64,0.08)',
            tension: 0.3,
            fill: false,
            pointRadius: 4,
          },
          {
            label: 'Zákazníci',
            data: this.rows.map(r => r.customers),
            borderColor: '#28a745',
            backgroundColor: 'rgba(40,167,69,0.08)',
            tension: 0.3,
            fill: false,
            pointRadius: 4,
          },
          {
            label: 'Jen registrovaní',
            data: this.rows.map(r => r.only_registered),
            borderColor: '#aaaaaa',
            backgroundColor: 'rgba(170,170,170,0.08)',
            tension: 0.3,
            fill: false,
            pointRadius: 4,
            borderDash: [4, 4],
          },
        ]
      }
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: { mode: 'index', intersect: false }
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } }
        }
      }
    },
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    fetchData() {
      this.loading = true
      axios.get(`/students_stats/cze_reg_data?period=${this.period}`)
        .then(response => {
          this.rows = response.data.data
          this.chartKey++
        })
        .catch(err => console.error(err))
        .finally(() => { this.loading = false })
    },

    switchPeriod(p) {
      if (this.period === p) return
      this.period = p
      this.fetchData()
    },

    formatLabel(row) {
      if (this.period === 'week') {
        const s = moment(row.period_start)
        const e = moment(row.period_end)
        return `${s.date()}.${s.month() + 1}. – ${e.date()}.${e.month() + 1}.`
      } else {
        const d = moment(row.period_start)
        return `${monthNames[d.month()]} ${d.year()}`
      }
    },

    convClass(val) {
      if (val >= 20) return 'conv-high'
      if (val >= 10) return 'conv-mid'
      return 'conv-low'
    },
  }
}
</script>

<template>
  <div class="cze-reg-page">

    <div class="page-header">
      <h1 class="heading">CZE Registrovaní</h1>
      <p class="subtitle">
        Kohorty nových B2C studentů (OJ + MJ CZ + EL CS, pouze CZ jazyk) — sledujeme jejich konverzi na trialistu a zákazníka.
      </p>
    </div>

    <!-- Přepínač týden / měsíc -->
    <div class="period-switch">
      <button
        :class="['switch-btn', { active: period === 'week' }]"
        @click="switchPeriod('week')"
      >Týdny</button>
      <button
        :class="['switch-btn', { active: period === 'month' }]"
        @click="switchPeriod('month')"
      >Měsíce</button>
    </div>

    <div v-if="loading" class="loading">Načítám data...</div>

    <template v-else-if="rows.length > 0">

      <!-- Graf -->
      <div class="chart-box">
        <Line
          :key="chartKey"
          :data="chartData"
          :options="chartOptions"
        />
      </div>

      <!-- Tabulka -->
      <div class="table-box">
        <table class="table table-bordered table-sm">
          <thead class="thead-light">
            <tr>
              <th>Období</th>
              <th class="text-right">Celkem reg.</th>
              <th class="text-right">Jen registrovaní</th>
              <th class="text-right">Trialisté</th>
              <th class="text-right">Zákazníci</th>
              <th class="text-right">% reg → trial</th>
              <th class="text-right">% reg → zákazník</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in [...rows].reverse()" :key="row.period_start">
              <td>{{ formatLabel(row) }}</td>
              <td class="text-right">{{ row.registered }}</td>
              <td class="text-right text-muted">{{ row.only_registered }}</td>
              <td class="text-right text-warning-dark">{{ row.trialists }}</td>
              <td class="text-right text-success">{{ row.customers }}</td>
              <td class="text-right" :class="convClass(row.conv_to_trial)">
                {{ row.registered > 0 ? row.conv_to_trial + ' %' : '—' }}
              </td>
              <td class="text-right" :class="convClass(row.conv_to_customer)">
                {{ row.registered > 0 ? row.conv_to_customer + ' %' : '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </template>

    <div v-else class="empty-state">
      Zatím nejsou k dispozici žádná data. Spusťte <code>rake statistics:refresh</code> pro první naplnění.
    </div>

  </div>
</template>

<style scoped>
.cze-reg-page {
  padding: 24px;
  max-width: 1100px;
}

.page-header {
  margin-bottom: 20px;
}

.heading {
  font-size: 1.6rem;
  font-weight: 700;
  color: #252626;
}

.subtitle {
  color: #828283;
  margin-top: 4px;
  font-size: 0.9rem;
}

.period-switch {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.switch-btn {
  padding: 6px 20px;
  border: 1px solid #5aacff;
  border-radius: 20px;
  background: white;
  color: #5aacff;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.15s, color 0.15s;
}

.switch-btn.active {
  background: #5aacff;
  color: white;
}

.switch-btn:hover:not(.active) {
  background: #eaf5ff;
}

.chart-box {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(241, 242, 244, 1);
  padding: 16px;
  height: 320px;
  margin-bottom: 24px;
}

.table-box {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(241, 242, 244, 1);
  padding: 16px;
  overflow-x: auto;
}

.loading,
.empty-state {
  color: #828283;
  padding: 40px;
  text-align: center;
}

.text-right {
  text-align: right;
}

.text-muted {
  color: #888;
}

.text-warning-dark {
  color: #c67a00;
}

.text-success {
  color: #28a745;
}

.conv-high {
  color: #28a745;
  font-weight: 600;
}

.conv-mid {
  color: #c67a00;
}

.conv-low {
  color: #999;
}
</style>
